import { API_BASE_URL, DEFAULT_COMPANY_ID } from './config';
import { apiFetch, getAuthToken } from './apiFetch';

export type JobDto = {
  id: string;
  companyId: string;
  title: string;
  description: string;
  department: string;            // free-text fallback / display label
  divisionId?: string;           // FK → divisions — structured org link
  orgLevelId?: string;           // FK → org_levels — seniority band
  hiringManagerUserId?: string;
  status: 'draft' | 'published' | 'closed';
  requiredCompetencies: Array<{
    id: string;
    competencyCode: string;
    minimumLevel: number;
    weight: number;
  }>;
  createdAt: string;
  updatedAt: string;
};

export type CandidateDto = {
  id: string;
  companyId: string;
  name: string;
  email: string;
  phone?: string;
  cvUrl?: string | null;          // raw MinIO storage key — truthy = CV file exists
  competencyProfile: Array<{ code: string; level: number; confidence?: number }>;
  createdAt: string;
  updatedAt: string;
};

export type VerificationMethod =
  | 'technical_test'
  | 'coding_assessment'
  | 'user_interview'
  | 'technical_interview'
  | 'competency_interview';

export const VERIFICATION_METHOD_LABELS: Record<VerificationMethod, string> = {
  technical_test:       'Technical Test',
  coding_assessment:    'Coding Assessment',
  user_interview:       'User Interview',
  technical_interview:  'Technical Interview',
  competency_interview: 'Competency Interview',
};

export type VerifiedCompetency = {
  code: string;
  claimedLevel: number;
  verifiedLevel: number;
  method: VerificationMethod;
  verifiedByUserId: string;
  verifiedAt: string;
  notes?: string;
};

export type ApplicationInterviewer = {
  userId: string;
  name: string;
  role?: string;
  stage: ApplicationDto['status'];
};

export type ApplicationBreakdown = {
  details: Array<{
    competencyCode: string;
    requiredLevel: number;
    candidateLevel: number;
    contribution: number;
  }>;
  totalWeight: number;
  matchedWeight: number;
};

export type ApplicationDto = {
  id: string;
  companyId: string;
  jobId: string;
  candidateId: string;
  status: 'new' | 'screening' | 'interview' | 'offer' | 'hired' | 'rejected';
  matchScore: number;
  breakdown?: ApplicationBreakdown;
  notes?: string;
  verifiedCompetencies: VerifiedCompetency[];
  hiringManagerUserId?: string;
  interviewers: ApplicationInterviewer[];
  appliedAt: string;
  updatedAt: string;
};

export type CandidateDetailDto = {
  candidate: CandidateDto;
  applications: Array<{
    application: ApplicationDto;
    job: { id: string; title: string; department: string };
  }>;
};

export async function listJobs(companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/jobs/company/${companyId}`);
  return response.json() as Promise<{ success: boolean; data?: { jobs: JobDto[] }; error?: string }>;
}

export async function getJob(jobId: string) {
  const response = await apiFetch(`${API_BASE_URL}/api/jobs/${jobId}`);
  return response.json() as Promise<{ success: boolean; data?: { job: JobDto }; error?: string }>;
}

export async function createJob(input: {
  companyId?: string;
  title: string;
  description?: string;
  department: string;
  divisionId?: string;
  orgLevelId?: string;
  hiringManagerUserId?: string;
  requiredCompetencies: Array<{ competencyCode: string; minimumLevel: number; weight: number }>;
}) {
  const response = await apiFetch(`${API_BASE_URL}/api/jobs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      title: input.title,
      description: input.description,
      department: input.department,
      divisionId: input.divisionId,
      orgLevelId: input.orgLevelId,
      hiringManagerUserId: input.hiringManagerUserId,
      requiredCompetencies: input.requiredCompetencies,
    }),
  });

  return response.json() as Promise<{ success: boolean; data?: { job: JobDto }; error?: string }>;
}

export async function publishJob(input: { companyId?: string; jobId: string }) {
  const response = await apiFetch(`${API_BASE_URL}/api/jobs/${input.jobId}/publish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
    }),
  });

  return response.json() as Promise<{ success: boolean; data?: { job: JobDto }; error?: string }>;
}

export async function listCandidates(companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/candidates/company/${companyId}`);
  return response.json() as Promise<{ success: boolean; data?: { candidates: CandidateDto[] }; error?: string }>;
}

export async function createCandidate(input: {
  companyId?: string;
  name: string;
  email: string;
  cvText?: string;
}) {
  const createResponse = await apiFetch(`${API_BASE_URL}/api/candidates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      name: input.name,
      email: input.email,
    }),
  });

  const created = (await createResponse.json()) as { success: boolean; data?: { candidate: CandidateDto }; error?: string };
  if (!created.success || !created.data?.candidate || !input.cvText?.trim()) {
    return created;
  }

  const uploadResponse = await apiFetch(`${API_BASE_URL}/api/candidates/${created.data.candidate.id}/upload-cv`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      rawText: input.cvText,
    }),
  });

  return uploadResponse.json() as Promise<{ success: boolean; data?: { candidate: CandidateDto }; error?: string }>;
}

export async function submitApplication(input: { companyId?: string; jobId: string; candidateId: string }) {
  const response = await apiFetch(`${API_BASE_URL}/api/applications`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      jobId: input.jobId,
      candidateId: input.candidateId,
    }),
  });

  return response.json() as Promise<{ success: boolean; data?: { application: ApplicationDto }; error?: string }>;
}

export async function listApplications(companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/applications/company/${companyId}`);
  return response.json() as Promise<{ success: boolean; data?: { applications: ApplicationDto[] }; error?: string }>;
}

export async function updateApplicationStatus(
  applicationId: string,
  status: ApplicationDto['status'],
  companyId = DEFAULT_COMPANY_ID,
) {
  const response = await apiFetch(`${API_BASE_URL}/api/applications/${applicationId}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, status }),
  });
  return response.json() as Promise<{ success: boolean; data?: { application: ApplicationDto }; error?: string }>;
}

export async function analyzeCV(input: {
  candidateId: string;
  companyId?: string;
  rawText: string;
}) {
  const response = await apiFetch(`${API_BASE_URL}/api/candidates/${input.candidateId}/upload-cv`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      rawText: input.rawText,
    }),
  });
  return response.json() as Promise<{ success: boolean; data?: { candidate: CandidateDto }; error?: string }>;
}

/**
 * Analyze a CV from an already-uploaded file (by fileId).
 */
export async function analyzeCVByFileId(input: {
  candidateId: string;
  companyId?: string;
  fileId: string;
}) {
  const response = await apiFetch(`${API_BASE_URL}/api/candidates/${input.candidateId}/upload-cv`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      fileId: input.fileId,
    }),
  });
  return response.json() as Promise<{ success: boolean; data?: { candidate: CandidateDto }; error?: string }>;
}

/**
 * Extract required competencies from a job description file (by fileId).
 */
export async function extractJdFromFile(input: {
  jobId: string;
  companyId?: string;
  fileId: string;
}) {
  const response = await apiFetch(`${API_BASE_URL}/api/jobs/${input.jobId}/extract-jd`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      fileId: input.fileId,
    }),
  });
  return response.json() as Promise<{
    success: boolean;
    data?: { competencies: Array<{ competencyCode: string; competencyName: string; minimumLevel: number; weight: number }>; count: number };
    error?: string;
  }>;
}

export async function getCandidate(candidateId: string, companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/candidates/${candidateId}?companyId=${companyId}`);
  return response.json() as Promise<{ success: boolean; data?: CandidateDetailDto; error?: string }>;
}

export async function updateApplicationNotes(
  applicationId: string,
  notes: string,
  companyId = DEFAULT_COMPANY_ID,
) {
  const response = await apiFetch(`${API_BASE_URL}/api/applications/${applicationId}/notes`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, notes }),
  });
  return response.json() as Promise<{ success: boolean; data?: { application: ApplicationDto }; error?: string }>;
}

export async function updateJob(
  jobId: string,
  input: {
    companyId: string;
    title?: string;
    description?: string;
    department?: string;
    divisionId?: string | null;
    orgLevelId?: string | null;
    hiringManagerUserId?: string | null;
  },
) {
  const response = await apiFetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return response.json() as Promise<{ success: boolean; data?: { job: JobDto }; error?: string }>;
}

/**
 * Build a stream URL for a candidate's CV — use with window.open() for browser downloads.
 * The server streams the PDF directly from MinIO, bypassing the internal MinIO hostname.
 * Returns null if no auth token is available.
 */
export function getCandidateCvStreamUrl(candidateId: string, companyId = DEFAULT_COMPANY_ID): string | null {
  const token = getAuthToken();
  if (!token) return null;
  return `${API_BASE_URL}/api/candidates/${candidateId}/cv-stream?companyId=${encodeURIComponent(companyId)}&token=${encodeURIComponent(token)}`;
}

export async function verifyCompetency(input: {
  applicationId: string;
  companyId?: string;
  code: string;
  claimedLevel: number;
  verifiedLevel: number;
  method: VerificationMethod;
  verifiedByUserId: string;
  notes?: string;
}) {
  const response = await apiFetch(`${API_BASE_URL}/api/applications/${input.applicationId}/verify-competency`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      code: input.code,
      claimedLevel: input.claimedLevel,
      verifiedLevel: input.verifiedLevel,
      method: input.method,
      verifiedByUserId: input.verifiedByUserId,
      notes: input.notes,
    }),
  });
  return response.json() as Promise<{ success: boolean; data?: { application: ApplicationDto }; error?: string }>;
}

export async function assignHiringManager(input: {
  applicationId: string;
  companyId?: string;
  hiringManagerUserId: string | null;
}) {
  const response = await apiFetch(`${API_BASE_URL}/api/applications/${input.applicationId}/hiring-manager`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      hiringManagerUserId: input.hiringManagerUserId,
    }),
  });
  return response.json() as Promise<{ success: boolean; data?: { application: ApplicationDto }; error?: string }>;
}

export async function upsertInterviewer(input: {
  applicationId: string;
  companyId?: string;
  userId: string;
  name: string;
  role?: string;
  stage: ApplicationDto['status'];
}) {
  const response = await apiFetch(`${API_BASE_URL}/api/applications/${input.applicationId}/interviewers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      userId: input.userId,
      name: input.name,
      role: input.role,
      stage: input.stage,
    }),
  });
  return response.json() as Promise<{ success: boolean; data?: { application: ApplicationDto }; error?: string }>;
}

export async function removeInterviewer(input: {
  applicationId: string;
  companyId?: string;
  userId: string;
  stage: ApplicationDto['status'];
}) {
  const companyId = input.companyId ?? DEFAULT_COMPANY_ID;
  const response = await apiFetch(
    `${API_BASE_URL}/api/applications/${input.applicationId}/interviewers/${input.userId}/${input.stage}?companyId=${encodeURIComponent(companyId)}`,
    { method: 'DELETE' },
  );
  return response.json() as Promise<{ success: boolean; data?: { application: ApplicationDto }; error?: string }>;
}

export async function listRankedCandidates(input: { companyId?: string; jobId: string }) {
  const companyId = input.companyId ?? DEFAULT_COMPANY_ID;
  const response = await apiFetch(`${API_BASE_URL}/api/jobs/${input.jobId}/ranked-candidates?companyId=${companyId}`);
  return response.json() as Promise<{
    success: boolean;
    data?: {
      rankedCandidates: Array<{
        application: ApplicationDto;
        candidate: CandidateDto;
      }>;
    };
    error?: string;
  }>;
}
